import { colors } from "theme/foundations/colors";
import RIVERS from "data/river-basins.json";
// a nice library for country names and codes :)
import countries from "i18n-iso-countries";

/** Helper function to determine the color scheme of the status badge
 *  */
export const getBadgeColor = (stageType) => {
  if (!stageType) return "gray";
  return colors.stagesFaded[stageType] || "gray";
};

/**
 * Handles a couple of formats - ISSSB, CDP
 * @param {string} issbReference e.g. 6(a)(ii) or 7.1.14
 * @returns
 */
export function numberForReference(issbReference) {
  if (!issbReference) return null;
  try {
    // convert e.g. (i) to 1, (ii) to (2)
    issbReference = issbReference.replace(/\(i+\)/, (m) => "(" + (m.length - 2) + ")");
    const parts = issbReference.split(/[.()]/).filter((p) => p); // remove ""s
    // if p is a-z, convert to 1-26
    let nums = parts.map((p) => {
      if (p.match(/^[a-z]$/)) {
        return p.charCodeAt(0) - 96;
      }
      return parseInt(p);
    });
    // pad nums to [a,b,c,d] if shorter than 4
    for (let i = nums.length; i < 4; i++) {
      nums.push(0);
    }
    // 100x weight the first nums, and then the second, etc. Which allows for upto 99 sub-parts (we do need more than 10)
    const v = nums.reduce((acc, num) => acc * 100 + num, 0);
    return v;
  } catch (e) {
    console.error("numberForReference error", e);
    return null;
  }
}
window.numberForReference = numberForReference; // debug

/**
 * Is this a multipart question & answer?
 * A function to standardise the check
 * @param {*} questionData
 */
export function isMultipartQuestion(questionData) {
  return questionData?.parts?.length > 0;
}

/**
 * Make a nice string for display in the UI (where we cant display the full form).
 * This looks for and uses AI draft summaries (`plain_text_version`) if present.
 *
 * Warning: This was written to summarise AI drafts. It is now also used in
 * Question list / AI Report citations to summarise answers, as we were pushed for
 * quick feature edits that needed an answer summary string. This can be off if
 * the user edits the answer (in which case the AI draft summary could stick
 * around but be wrong).
 * This is considered a corner case for now. See Jira ESG-3544.
 *
 * @param {string|object[]} answer
 * @returns {string}
 */
export function multipartAnswerToString(answer) {
  if (answer === null || answer === undefined || answer === "") {
    return "";
  }
  try {
    const answerParts = typeof answer === "string" ? JSON.parse(answer) : answer;
    // Did the AI make summary text? If so use that
    // Assume: a gap in summary-text can be ignored -- the answers are generated as a block, so one summary-text for all is a reasonable AI output
    const summary_texts = answerParts.map((p) => p.plain_text_version).filter((t) => t);
    if (summary_texts.length > 0) {
      return "Summary: " + summary_texts.join("\n\n");
    }
    // turn the answer parts into a string
    let all = "";
    for (let part of answerParts) {
      let a = part.answerValue;
      if (a === undefined) a = part.answer || part.answerPart; // HACK if the AI used a different label (June 2025)
      if (a === null || a === undefined || a === "") {
        continue; // Note: dont skip false or 0, because they are valid answers
      }
      // Note: part.type is usually _unset_ here (it gets set where answer is merged with the questionParts)
      if (Array.isArray(a)) {
        console.log("table to string", part);
        all += a.map((row) => multipartAnswerToString(row)).join("\n");
        continue;
      }
      all += part.questionPart + "\n";
      if (part.type === "boolean") {
        all += a ? "Yes" : "No";
      } else if (part.type === "metric") {
        all += a + " " + part.unit;
      } else if (part.type === "percentage") {
        all += a + "%";
      } else if (part.type === "number") {
        all += a;
      } else {
        all += a;
      }
      all += "\n\n";
    }
    return all;
  } catch (e) {
    console.log("(swallow) Error json-parsing answer", answer, e);
    return "" + answer;
  }
}

/**
 *
 * @returns {string[]}
 */
export function getListOfRivers() {
  // remove the comment line
  return RIVERS.filter((r) => !r.startsWith("//"));
}

/**
 *
 * @returns {Object<string, string>}
 */
export function getCountryByISO3166Code() {
  // Register the languages you want to use
  countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
  const name4code = countries.getNames("en");
  return name4code;
}
